// ─── Kiosk v2 section card ──────────────────────────────────────────────
//
// A small expandable card used inside the Kiosk Health module to render one of
// the 14 v2 sections. Headers show title + status badge + issue count and
// (optionally) changed-fields count. Body shows a Field / Current / Expected /
// Status grid or, for package_policy and app_permissions, a dedicated row
// renderer that respects the request-aware semantic.
//
// Pure presentational — health rules live in utils/kioskHealth.ts. No data
// fetching, no side effects.

import { useEffect, useState, type KeyboardEvent } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type {
  AppPermissionRow,
  KioskFieldDisplay,
  KioskV2SectionResult,
  KioskV2SectionStatus,
  PackagePolicyRow,
} from '../../utils/kioskHealth';

// ─── Tone palette (matches HealthModuleCard + DeviceHealthTab v1 tones) ───

const STATUS_PILL: Record<KioskV2SectionStatus, { pill: string; dot: string; label: string }> = {
  ok: { pill: 'border-green-700/50 bg-green-900/30 text-green-300', dot: 'bg-green-400', label: 'OK' },
  info: { pill: 'border-slate-700 bg-slate-800/50 text-slate-300', dot: 'bg-slate-400', label: 'Info' },
  warn: { pill: 'border-amber-700/50 bg-amber-900/30 text-amber-300', dot: 'bg-amber-400', label: 'Warning' },
  fail: { pill: 'border-red-700/50 bg-red-900/30 text-red-300', dot: 'bg-red-400', label: 'Critical' },
  incomplete: { pill: 'border-slate-700 bg-slate-800/50 text-slate-400', dot: 'bg-slate-400', label: 'Incomplete' },
};

const FIELD_VALUE_TONE: Record<KioskFieldDisplay['tone'], string> = {
  ok: 'text-green-400',
  warn: 'text-amber-400',
  fail: 'text-red-400',
  unreadable: 'text-slate-500 italic',
  info: 'text-slate-300',
};

function StatusPill({ status, size = 'md' }: { status: KioskV2SectionStatus; size?: 'md' | 'sm' }) {
  const s = STATUS_PILL[status];
  const sizing = size === 'sm' ? 'px-2 py-0.5 text-[10px] gap-1' : 'px-2.5 py-0.5 text-xs gap-1.5';
  const dotSize = size === 'sm' ? 'h-1 w-1' : 'h-1.5 w-1.5';
  return (
    <span className={`inline-flex items-center rounded-full border font-medium ${s.pill} ${sizing}`}>
      <span className={`rounded-full ${s.dot} ${dotSize}`} />
      {s.label}
    </span>
  );
}

// ─── Field rows ────────────────────────────────────────────────────────

function FieldRow({
  field,
  changed,
}: {
  field: KioskFieldDisplay;
  changed?: boolean;
}) {
  return (
    <div
      className={`grid grid-cols-[1fr,auto,auto,auto] items-center gap-3 px-3 py-1.5 text-sm ${
        changed ? 'bg-amber-900/10' : ''
      }`}
    >
      <span className="text-slate-300 truncate" title={field.label}>{field.label}</span>
      <span className={`font-mono text-xs ${FIELD_VALUE_TONE[field.tone]}`}>{field.value}</span>
      {field.tone === 'ok' || field.tone === 'info' ? (
        <span className="text-[10px] text-slate-500 font-mono">{field.expected}</span>
      ) : (
        <span className="text-[10px] text-slate-500 font-mono">expected {field.expected}</span>
      )}
      {changed ? (
        <span className="text-[10px] text-amber-400/80 font-medium">changed</span>
      ) : (
        <span />
      )}
    </div>
  );
}

// ─── app_permissions custom row ────────────────────────────────────────

function AppPermissionRowRender({ row }: { row: AppPermissionRow }) {
  const installedLabel = row.installed === null ? 'unreadable' : row.installed ? 'Installed' : 'Not installed';
  return (
    <div className="px-3 py-2 border-t border-slate-700/40 first:border-t-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-200">{row.label}</span>
          <StatusPill status={row.status} size="sm" />
        </div>
        <span className={`text-xs font-mono ${row.installed === false ? 'text-amber-400' : row.installed === null ? 'text-slate-500 italic' : 'text-slate-400'}`}>
          {installedLabel}
        </span>
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {row.groups.map((g) => {
          const tone = FIELD_VALUE_TONE[g.tone];
          const requestedText = g.requested === true ? 'requested' : g.requested === false ? 'not requested' : g.requested === null ? 'unreadable' : '—';
          const grantedText = g.granted === null ? '—' : g.granted ? 'granted' : 'denied';
          return (
            <span
              key={g.id}
              className={`inline-flex items-center gap-1 rounded-md border border-slate-700 bg-slate-800/40 px-1.5 py-0.5 text-[10px] font-mono ${tone}`}
              title={`${g.label}: ${requestedText} · ${grantedText}`}
            >
              <span className="text-slate-300">{g.label}</span>
              <span>·</span>
              <span>
                {g.requested === false || g.requested === null ? requestedText : grantedText}
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

// ─── package_policy custom row ─────────────────────────────────────────

function PackagePolicyRowRender({ row }: { row: PackagePolicyRow }) {
  const installedText = row.installed === null ? 'unreadable' : row.installed ? 'installed' : 'not installed';
  const hiddenText =
    row.hidden === null ? '—' : row.hidden ? 'hidden' : 'unhidden';
  const expectedText =
    row.expectedHidden === 'either'
      ? '—'
      : row.expectedHidden
        ? 'hidden if installed'
        : 'unhidden if installed';
  const installedTone = row.installed === false || row.installed === null ? 'text-slate-500' : 'text-slate-300';
  const hiddenTone =
    row.status === 'fail'
      ? 'text-red-400'
      : row.status === 'warn'
        ? 'text-amber-400'
        : row.status === 'info'
          ? 'text-slate-500 italic'
          : 'text-green-400';
  return (
    <div className="grid grid-cols-[1fr,auto,auto,auto] items-center gap-3 px-3 py-1.5 text-sm border-t border-slate-700/40 first:border-t-0">
      <span className="font-mono text-xs text-slate-300 truncate" title={row.pkg}>{row.pkg}</span>
      <span className={`text-xs font-mono ${installedTone}`}>{installedText}</span>
      <span className={`text-xs font-mono ${hiddenTone}`}>{hiddenText}</span>
      <span className="text-[10px] text-slate-500 font-mono">{expectedText}</span>
    </div>
  );
}

// ─── Main section card ────────────────────────────────────────────────

export interface KioskV2SectionCardProps {
  section: KioskV2SectionResult;
  changedCount?: number;
  /** Override default expansion behavior. When omitted, expanded iff status is not 'ok'. */
  forceExpanded?: boolean;
  /** Hint shown next to the section header (used to flag auto-promoted advanced sections). */
  hint?: string;
}

export default function KioskV2SectionCard({
  section,
  changedCount,
  forceExpanded,
  hint,
}: KioskV2SectionCardProps) {
  const defaultExpanded =
    forceExpanded ??
    (section.status !== 'ok' || (changedCount != null && changedCount > 0));
  const [expanded, setExpanded] = useState<boolean>(defaultExpanded);

  // When the source section changes (e.g. refetch), re-apply default expansion
  // so newly-drifted sections auto-expand without requiring a remount.
  useEffect(() => {
    setExpanded(defaultExpanded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section.id, section.status, changedCount, forceExpanded]);

  const toggle = () => setExpanded((v) => !v);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  const hasContent =
    section.fields.length > 0 || section.special != null;

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/40">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={onKey}
        className="flex items-center justify-between gap-3 px-3 py-2 cursor-pointer hover:bg-slate-700/20 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="flex h-4 w-4 flex-none items-center justify-center text-slate-400">
            {expanded ? (
              <ChevronDownIcon className="h-3.5 w-3.5" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5" />
            )}
          </span>
          <span className="text-sm font-medium text-slate-200 truncate">{section.title}</span>
          <StatusPill status={section.status} size="sm" />
          {hint && <span className="text-[10px] text-amber-400/80 italic">{hint}</span>}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500 whitespace-nowrap">
          {section.isIncomplete && (
            <span className="text-amber-400/80">read incomplete</span>
          )}
          {section.issueCount > 0 && (
            <span>
              {section.issueCount} {section.issueCount === 1 ? 'issue' : 'issues'}
            </span>
          )}
          {changedCount != null && changedCount > 0 && (
            <span className="text-amber-400/80">{changedCount} changed</span>
          )}
        </div>
      </div>
      {expanded && hasContent && (
        <div className="border-t border-slate-700/50">
          {section.special?.kind === 'app_permissions' ? (
            <div>
              {section.special.apps.map((row) => (
                <AppPermissionRowRender key={row.appId} row={row} />
              ))}
            </div>
          ) : section.special?.kind === 'package_policy' ? (
            <div className="py-1">
              {section.special.packages.map((row) => (
                <PackagePolicyRowRender key={row.pkg} row={row} />
              ))}
            </div>
          ) : (
            <div className="py-1">
              {section.fields.map((f, i) => (
                <FieldRow key={`${section.id}-${f.label}-${i}`} field={f} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
